var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import TENANT_ID from '../config/TenantId';
import { Metric } from '../metrics';
import { isAEAInstalled, isMDMValidForAEAAuth, toErrorMessage } from '../util';
import { getAuthenticator } from './authenticatorFactory';
import { AEANotInstalledError, MDMInvalidError, NonRetryableError } from './error';
const getInitializedAuthenticator = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!(yield isAEAInstalled())) {
        Metric('stepUpAuth.AEANotInstalled', TENANT_ID).putAndLog();
        throw new AEANotInstalledError();
    }
    if (!(yield isMDMValidForAEAAuth())) {
        Metric('stepUpAuth.MDMInvalid', TENANT_ID).putAndLog();
        throw new MDMInvalidError();
    }
    const authenticator = getAuthenticator();
    if (!authenticator) {
        Metric('stepUpAuth.notInitialized', TENANT_ID).putAndLog();
        throw new NonRetryableError('authenticator_not_initialized');
    }
    return authenticator;
});
/**
 * Checks whether the session has a valid step up access token available. It attempts a
 * refresh if token has expired, but does not trigger a reauthentication process
 * if both access and refresh token are invalid
 *
 * @returns true if the access or refresh token is available and valid; otherwise false
 */
export const isStepUpTokenRefreshable = () => __awaiter(void 0, void 0, void 0, function* () {
    Metric('isStepUpTokenRefreshable.count', TENANT_ID).putAndLog();
    try {
        const authenticator = yield getInitializedAuthenticator();
        yield authenticator.getAccessToken();
        return true;
    }
    catch (_a) {
        return false;
    }
});
/**
 * Checks whether the session has a valid step up access token available
 *
 * @returns true if the access token is available and valid; otherwise false
 */
export const isStepUpTokenValid = () => __awaiter(void 0, void 0, void 0, function* () {
    Metric('isStepUpTokenValid.count', TENANT_ID).putAndLog();
    try {
        const authenticator = yield getInitializedAuthenticator();
        return yield authenticator.isAccessTokenValid();
    }
    catch (_b) {
        Metric('isStepUpTokenValid.invalid', TENANT_ID).putAndLog();
        return false;
    }
});
/**
 * Initiates step up authentication
 *
 * If there is a valid access token it is immediately returned
 * If there is an invalid access token attempt to refresh
 * If token refresh fails attempt to reauthorize via step up
 *
 * @returns a valid step up access token
 * @throws AEANotInstalledError, NonRetryableError, RetryableError
 */
export const initiateStepUpAuth = () => __awaiter(void 0, void 0, void 0, function* () {
    Metric('initiateStepUpAuth.count', TENANT_ID).putAndLog();
    const authenticator = yield getInitializedAuthenticator();
    try {
        const token = yield authenticator.getAccessToken();
        if (!token) {
            Metric('initiateStepUpAuth.nullToken', TENANT_ID).putAndLog();
            throw new NonRetryableError('null_access_token');
        }
        Metric('initiateStepUpAuth.refreshSuccess', TENANT_ID).putAndLog();
        return token;
    }
    catch (refreshError) {
        if (refreshError instanceof NonRetryableError) {
            try {
                const token = yield authenticator.authorize();
                Metric('initiateStepUpAuth.authorizeSuccess', TENANT_ID).putAndLog();
                return token;
            }
            catch (authorizeError) {
                Metric('initiateStepUpAuth.authorizeError', TENANT_ID, {
                    attributes: { error: toErrorMessage(authorizeError) },
                }).putAndLog();
                throw authorizeError;
            }
        }
        Metric('initiateStepUpAuth.refreshError', TENANT_ID, {
            attributes: { error: toErrorMessage(refreshError) },
        }).putAndLog();
        throw refreshError;
    }
});
//# sourceMappingURL=StepUpAuthenticator.js.map